import { useNavigate } from "react-router-dom"
import { observer } from "mobx-react-lite"

import type { ILock } from "@/domains/lock"
import { Checkbox } from "@/components/checkbox"
import { ShowWhenTrue } from "@/components/conditionals"
import { formatLockEnds } from "@/components/lock"
import { Compound as Table } from "@/components/table"
import { TokenIcon } from "@/components/token-icon"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { DisplayLevel } from "@/domains/common/components/display-level"
import { Lozenge } from "@/domains/common/components/lozenge"
import { BodyXs } from "@/domains/design/components/body-xs"
import { ChevronRightIcon, TickIcon } from "@/domains/icons"
import { NoIcon } from "@/domains/icons/huge-icons/monotone/no"
import { formatCurrency, formatPercentage } from "@/domains/library"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useGovernanceToken } from "@/hooks/use-governance-token"
import { useVestedToken } from "@/hooks/use-vested-token"

/**
 * Lock table is ONLY displayed on Tablet and up!
 */
export const LockTableRow = observer(({ nft }: { nft: ILock }) => {
  const navigate = useNavigate()
  const scaleApp = useScaleApplication()
  const chainConfig = useChainConfig()
  const vestedToken = useVestedToken()
  const governanceToken = useGovernanceToken()

  const veTotalSupply = scaleApp.Stats.veTotalSupply

  return (
    <Table.Tr
      key={nft.id}
      className="group cursor-pointer border-t border-t-p400-light bg-p400"
      onClick={() => {
        navigate(`/lock/${nft.id}/increase-amount`)
      }}
    >
      <Table.Td className="px-0 py-0 group-hover:bg-p300 sm:px-0">
        <div className="flex h-full w-full items-center justify-center">
          <ChevronRightIcon size={16} className="text-t300 group-hover:text-t200" />
        </div>
      </Table.Td>
      <Table.Td>
        <div
          className="relative flex h-full items-start gap-2"
          onClick={(event: React.MouseEvent<HTMLDivElement>) => {
            event.stopPropagation()
          }}
        >
          <Checkbox
            id={String(nft.id)}
            checked={scaleApp.Pages.Lock.selectedNfts.includes(nft.id)}
            onChange={() => {
              scaleApp.Pages.Lock.toggleNftSelection(nft.id)
            }}
            disabled={nft.hasExpired}
          />
        </div>
      </Table.Td>
      <Table.Td>
        <NftInfo nft={nft} />
      </Table.Td>
      <Table.Td>
        <VotingPower nft={nft} symbol={vestedToken.symbol} />
      </Table.Td>
      <Table.Td>
        <Balance nft={nft} symbol={governanceToken.symbol} />
      </Table.Td>
      <Table.Td>
        <Power nft={nft} />
      </Table.Td>
      <Table.Td className="text-right">
        <Voted nft={nft} />
      </Table.Td>
      <Table.Td className="text-right">
        <Expires nft={nft} />
      </Table.Td>
      <Table.Td className="text-right">
        <Influence nft={nft} veTotalSupply={veTotalSupply} />
      </Table.Td>

      <Table.Td>
        <Level nft={nft} />
      </Table.Td>
    </Table.Tr>
  )
})

const NftInfo = ({ nft }: { nft: ILock }) => {
  const governanceToken = useGovernanceToken()
  const chainConfig = useChainConfig()
  return (
    <div className="flex items-center gap-2 py-2">
      <div className="flex items-center gap-2">
        <TokenIcon token={governanceToken} square={32} calledFrom="LockPage" />
        <div>
          <BodyXs>#{nft.id}</BodyXs>
          <BodyXs className="text-t300">NFT</BodyXs>
        </div>
      </div>
    </div>
  )
}
const Balance = ({ nft, symbol }: { nft: ILock; symbol: string }) => {
  return (
    <div className="flex flex-col items-end justify-center gap-0.5">
      <BodyXs As="span">{formatCurrency(nft.lockAmount, 4)}</BodyXs>{" "}
      <BodyXs As="span" className="text-t300">
        {symbol}
      </BodyXs>
    </div>
  )
}
const Expires = observer(({ nft }: { nft: ILock }) => {
  return (
    <div className="flex justify-end">
      <ShowWhenTrue when={nft.hasExpired}>
        <Lozenge color="error" size="xs">
          Expired
        </Lozenge>
      </ShowWhenTrue>
      <ShowWhenTrue when={nft.isMaxLocked}>
        <Lozenge color="blue" size="xs">
          Max Locked
        </Lozenge>
      </ShowWhenTrue>
      <ShowWhenTrue when={!nft.hasExpired && !nft.isMaxLocked}>
        <BodyXs As="span" className="whitespace-nowrap">
          {formatLockEnds(nft.lockEnds)}
        </BodyXs>
      </ShowWhenTrue>
    </div>
  )
})
const Influence = ({ nft, veTotalSupply }: { nft: ILock; veTotalSupply: number }) => {
  return <BodyXs As="span">{formatPercentage(nft.calculateInfluence(veTotalSupply), 4)}</BodyXs>
}
const Level = ({ nft }: { nft: ILock }) => {
  return (
    <div className="flex justify-center">
      <DisplayLevel lockValue={Number(nft.lockValue)} />
    </div>
  )
}
export const Power = ({ nft }: { nft: ILock }) => {
  return (
    <div className="flex h-[24px] items-center overflow-hidden rounded-xl border-2 border-p200-light bg-p200 text-xxs">
      <div
        className="rounded-l-xl rounded-r-sm bg-[#1a56cd] px-2 py-1 text-center"
        style={{ width: `${nft.power * 100}%` }}
      >
        {formatPercentage(nft.power, 2)}
      </div>
    </div>
  )
}
const Voted = observer(({ nft }: { nft: ILock }) => {
  return (
    <div className="flex justify-center">
      {nft.hasVotedThisEpoch ? (
        <TickIcon size={16} className="text-t300" />
      ) : (
        <NoIcon size={16} className="text-t300" />
      )}
    </div>
  )
})
const VotingPower = ({ nft, symbol }: { nft: ILock; symbol: string }) => {
  return (
    <div className="flex flex-col items-end justify-center gap-0.5">
      <BodyXs As="span">{formatCurrency(nft.lockValue, 4)}</BodyXs>{" "}
      <BodyXs As="span" className="text-t300">
        {symbol}
      </BodyXs>
    </div>
  )
}
